/**
 * Shared utilities for org-aware field provider processing.
 * Used by both the useOrgAwareProviders hook and IssueFieldFilterQueryParser.
 */

/**
 * Extract org names from raw query string using regex patterns.
 * Supports both org: and repo: filters, extracting org from repo format.
 *
 * @param rawQuery - The raw search query string
 * @returns Array of org names found in the query
 *
 * @example
 * extractOrgsFromRawQuery('org:github repo:microsoft/repo')
 * // returns ['github', 'microsoft']
 */
export function extractOrgsFromRawQuery(rawQuery: string): string[] {
  const orgs = new Set<string>()

  // Match org patterns like: org:github, org:"github-enterprise"
  const orgMatches = rawQuery.match(/org:(?:"([^"]+)"|([^\s]+))/g)
  if (orgMatches) {
    for (const match of orgMatches) {
      // Extract the value after org:
      const colonIndex = match.indexOf(':')
      const value = match.substring(colonIndex + 1)
      // Remove quotes if present
      const orgValue = value.replace(/^"(.*)"$/, '$1')
      if (orgValue) orgs.add(orgValue)
    }
  }

  // Match repo patterns like: repo:github/subteam, repo:"github/subteam"
  const repoMatches = rawQuery.match(/repo:(?:"([^"]+)"|([^\s]+))/g)
  if (repoMatches) {
    for (const match of repoMatches) {
      // Extract the value after repo:
      const colonIndex = match.indexOf(':')
      const value = match.substring(colonIndex + 1)
      // Remove quotes if present
      const repoValue = value.replace(/^"(.*)"$/, '$1')
      // Extract org from repo format "owner/repo"
      const orgFromRepo = repoValue.split('/')[0]
      if (orgFromRepo) orgs.add(orgFromRepo)
    }
  }

  return Array.from(orgs).filter(Boolean)
}

/**
 * Extract org name from a field provider key.
 *
 * @param providerKey - The provider key (e.g., "field.priority-github")
 * @returns The org name or null if not found
 *
 * @example
 * extractOrgFromProviderKey('field.priority-github') // returns 'github'
 * extractOrgFromProviderKey('field.priority') // returns null
 */
export function extractOrgFromProviderKey(providerKey: string): string | null {
  const match = providerKey.match(/^field\..+-(.+)$/)
  return match?.[1] ?? null
}

/**
 * Check if a provider key represents a field provider.
 *
 * @param key - The provider key to check
 * @returns True if this is a field provider key
 *
 * @example
 * isFieldProviderKey('field.priority-github') // returns true
 * isFieldProviderKey('field.priority') // returns true
 * isFieldProviderKey('state') // returns false
 */
export function isFieldProviderKey(key: string): boolean {
  return key.startsWith('field.') && key !== 'field.'
}

/**
 * Clean a field provider key by removing org suffix.
 * Only removes suffix if the org is in the provided context.
 *
 * @param providerKey - The original provider key
 * @param orgContext - Set of valid org names from context
 * @returns The cleaned key
 *
 * @example
 * cleanFieldProviderKey('field.priority-github', new Set(['github']))
 * // returns 'field.priority'
 *
 * cleanFieldProviderKey('field.priority-github', new Set(['microsoft']))
 * // returns 'field.priority-github' (unchanged)
 */
export function cleanFieldProviderKey(providerKey: string, orgContext: Set<string>): string {
  const orgMatch = providerKey.match(/^(field\..+)-(.+)$/)
  if (orgMatch) {
    const [, baseKey, potentialOrg] = orgMatch
    // Only remove the suffix if it's one of the known orgs from our context
    if (baseKey && potentialOrg && orgContext.has(potentialOrg)) {
      return baseKey
    }
  }
  return providerKey
}

/**
 * Clean display name by removing org suffix in parentheses.
 *
 * @param displayName - The original display name
 * @returns The cleaned display name
 *
 * @example
 * cleanDisplayName('Priority (github)') // returns 'Priority'
 * cleanDisplayName('Priority') // returns 'Priority' (unchanged)
 */
export function cleanDisplayName(displayName?: string): string {
  if (!displayName) return ''
  return displayName.replace(/\s*\([^)]+\)\s*$/, '') || displayName
}

/**
 * Check if a provider key matches any org in the context.
 * Used to determine if a field provider should be included.
 *
 * @param providerKey - The provider key to check
 * @param orgContext - Set of org names from query context
 * @returns True if the provider matches any org in context
 *
 * @example
 * providerMatchesOrgContext('field.priority-github', new Set(['github']))
 * // returns true
 *
 * providerMatchesOrgContext('field.priority-microsoft', new Set(['github']))
 * // returns false
 */
export function providerMatchesOrgContext(providerKey: string, orgContext: Set<string>): boolean {
  if (!isFieldProviderKey(providerKey)) {
    return true // Non-field providers always match
  }

  return Array.from(orgContext).some((org: string) => providerKey.includes(`-${org}`))
}

/**
 * Extract valid org names from field provider keys.
 * Useful for validating org context against available providers.
 *
 * @param providerKeys - Array of provider keys to analyze
 * @returns Set of org names found in field providers
 *
 * @example
 * getValidOrgsFromProviders(['field.priority-github', 'field.status-microsoft', 'state'])
 * // returns Set(['github', 'microsoft'])
 */
export function getValidOrgsFromProviders(providerKeys: string[]): Set<string> {
  const orgs = new Set<string>()

  for (const key of providerKeys) {
    if (isFieldProviderKey(key)) {
      const org = extractOrgFromProviderKey(key)
      if (org) {
        orgs.add(org)
      }
    }
  }

  return orgs
}
